import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { forkJoin, map, switchMap } from 'rxjs';
import { PersonLoginService } from '../services/person-login-service';
import { PersonService } from '../services/person-service';
import { StringUtils } from '../utils/string-utils';
import { Group } from './group';
import { PersonLogin } from './person-login';
import { GroupService } from '../services/group.service';

@Component({
  selector: 'person-groups',
  templateUrl: './person-groups.component.html',
  styleUrls: ['./person-groups.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner],
})
export class PersonGroupsComponent implements OnInit {
  readonly personId = input.required<string>({ alias: 'person-id' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly personService = inject(PersonService);
  private readonly groupService = inject(GroupService);
  private readonly router = inject(Router);
  readonly allGroups = signal<Group[]>([]);
  readonly personLogin = signal<PersonLogin | undefined>(undefined);
  readonly selectedGroup = signal<Group | undefined>(undefined);
  readonly selectedIsAssigned = signal<boolean>(false);
  readonly availableFilter = signal<string>('');
  readonly assignedFilter = signal<string>('');
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly assignedGroups = computed(() => this.sortGroups(this.personLogin()?.groups ?? []));
  readonly availableGroups = computed(() => this.getAvailableGroups());
  readonly filteredAvailableGroups = computed(() => this.filterGroups(this.availableGroups(), this.availableFilter()));
  readonly filteredAssignedGroups = computed(() => this.filterGroups(this.assignedGroups(), this.assignedFilter()));
  readonly personName = computed(() => this.personLogin()?.person?.description ?? '');

  ngOnInit(): void {
    if (this.personLoginService.isAdmin()) {
      this.isBusy.set(true);
      forkJoin({
        groups: this.groupService.getAllGroups(),
        personLogin: this.personLoginService.getPersonLogin(this.personId()),
      }).subscribe({
        next: (result) => {
          this.allGroups.set(result.groups ?? []);
          this.personLogin.set(result.personLogin ?? undefined);
          if (!result.personLogin) {
            this.errorMessage.set('This person does not have a login record');
          }
          this.isBusy.set(false);
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
    } else {
      this.router.navigate(['person']);
    }
  }

  selectAvailableGroup(group: Group): void {
    this.selectedGroup.set(group);
    this.selectedIsAssigned.set(false);
  }

  selectAssignedGroup(group: Group): void {
    this.selectedGroup.set(group);
    this.selectedIsAssigned.set(true);
  }

  isSelected(group: Group): boolean {
    return this.selectedGroup()?.id === group.id;
  }

  canAdd(): boolean {
    return !this.isBusy() && !!this.personLogin() && !!this.selectedGroup() && !this.selectedIsAssigned();
  }

  canRemove(): boolean {
    return !this.isBusy() && !!this.personLogin() && !!this.selectedGroup() && this.selectedIsAssigned();
  }

  addGroup(): void {
    if (this.canAdd()) {
      this.saveGroups([...this.assignedGroups(), this.selectedGroup()!]);
    }
  }

  removeGroup(): void {
    if (this.canRemove()) {
      const removedId = this.selectedGroup()!.id;
      this.saveGroups(this.assignedGroups().filter((group) => group.id !== removedId));
    }
  }

  onAvailableFilter(event: Event): void {
    this.availableFilter.set((event.target as HTMLInputElement).value);
  }

  onAssignedFilter(event: Event): void {
    this.assignedFilter.set((event.target as HTMLInputElement).value);
  }

  backToPersonDetail(): void {
    this.router.navigate(['person/' + this.personId()]);
  }

  private getAvailableGroups(): Group[] {
    const assignedIds = new Set(this.assignedGroups().map((group) => group.id));
    return this.sortGroups(this.allGroups().filter((group) => !assignedIds.has(group.id)));
  }

  private saveGroups(groups: Group[]): void {
    this.isBusy.set(true);
    this.errorMessage.set('');
    const updateUser = this.personLoginService.getCachedLogin()!.person.userLogin;
    this.personLoginService.savePersonLogin({ ...this.personLogin()!, groups }).pipe(
      switchMap((saved) => this.personService.save({ ...saved.person, updateUser, updateTimestamp: new Date() }).pipe(map((person) => ({ ...saved, person })))),
    ).subscribe({
      next: (result) => {
        this.personLogin.set(result);
        this.selectedGroup.set(undefined);
        this.isBusy.set(false);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private sortGroups(groups: Group[]): Group[] {
    return [...groups].sort((first, second) => (first.name ?? '').localeCompare(second.name ?? ''));
  }

  private filterGroups(groups: Group[], filter: string): Group[] {
    const term = filter.trim().toUpperCase();
    if (term.length > 0) {
      return groups.filter((group) => (group.name ?? '').toUpperCase().includes(term) || (group.description ?? '').toUpperCase().includes(term));
    }
    return groups;
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }
}
